import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import GatsbyImage from 'gatsby-image'
import { useInView } from 'react-intersection-observer'
import { LayoutContext, useUI } from '../../contexts'
import Video from '../shared/Video'
import { SiteLink } from '../shared/SiteLink'
import ArtDirectingImage from '../shared/ArtDirectingImage'
import {
  useLandingpageHeroClickedTracking,
  useModuleViewedTracking,
} from '../../hooks/tracking'
import RichText from '../RichText'
import { Icon } from '../shared/Icon'
import { desktopVW, mobileVW } from '../../styles'
import { useIsMobile } from '../../hooks/useIsMobile'

const HeroBlock = ({
  heroImage,
  heroImageMobile,
  heroCopy,
  heroVideoCopy,
  heroVideo,
  heroVideoMobile,
  heroButton,
  heroMobileButtons,
  heroVideoLink,
  heroVideoType,
  location,
  position,
  heroCopyColor,
}) => {
  const { node_locale, currency } = useContext(LayoutContext)
  const isMobile = useIsMobile()

  // IE doesn't support intersection observer, breaks the site
  const isIE =
    /*@cc_on!@*/ false ||
    (typeof window !== `undefined` && !!document.documentMode)
  const [ref, inView] = isIE ? [] : useInView({ triggerOnce: true })

  useEffect(() => {
    if (!isIE) {
      if (inView) {
        useModuleViewedTracking({
          currency: currency?.code,
          locale: node_locale,
          sectionType: 'Hero Block',
          order: position,
        })
      }
    }
  }, [!isIE && inView])

  const [modal, setModal] = useState(false)

  return (
    <StyledHero heroVideoType={heroVideoType} ref={ref}>
      <MainWrapper>
        {heroVideoType && heroVideoType === 'Full-hero video' ? (
          <>
            <DesktopVideoContainer>
              <StyledFullHeroVideo
                webm={heroVideo.videoWebm.file.url}
                mp4={heroVideo.videoMp4.file.url}
                poster={heroVideo?.poster?.fluid}
                modal={modal}
              ></StyledFullHeroVideo>
            </DesktopVideoContainer>
            {heroVideoMobile ? (
              <MobileVideoContainer>
                <StyledFullHeroVideo
                  webm={heroVideoMobile?.webm?.file.url}
                  mp4={heroVideoMobile?.videoMp4?.file.url}
                  poster={heroVideo?.poster?.fluid}
                  modal={modal}
                ></StyledFullHeroVideo>
              </MobileVideoContainer>
            ) : (
              <StyledFullHeroVideo
                webm={heroVideo.videoWebm.file.url}
                mp4={heroVideo.videoMp4.file.url}
                poster={heroVideo?.poster?.fluid}
                modal={modal}
              ></StyledFullHeroVideo>
            )}
          </>
        ) : (
          <>
            <StyledImage
              fluid={ArtDirectingImage(
                heroImageMobile?.fluid,
                heroImage?.fluid,
              )}
              alt={heroImage.title}
              loading='eager'
            />
          </>
        )}
        <Copy heroVideoType={heroVideoType}>
          <CopyHeader
            color={heroCopyColor?.color[0]}
            heroVideoType={heroVideoType}
          >
            <RichText json={heroCopy} />
            {heroVideoType && heroVideoType === 'Full-hero video' ? (
              <SoundButton
                onClick={() => {
                  setModal(!modal)
                }}
              >
                <Icon name={modal ? 'mute' : 'unmute'} />
              </SoundButton>
            ) : null}
          </CopyHeader>

          {heroButton && (
            <CopyButton
              className={heroMobileButtons ? 'hide-on-mobile' : ''}
              to={heroButton.target.slug}
              extension={heroButton.linkExtension}
              onClick={() =>
                useLandingpageHeroClickedTracking({
                  currency: currency?.code,
                  locale: node_locale,
                  linkType: 'Button',
                  label: heroButton.title,
                  title: heroCopy,
                  destination: `/${node_locale}/${heroButton.target.slug}/`,
                })
              }
              state={{ referrer: location.pathname }}
            >
              {heroButton.title}
            </CopyButton>
          )}
          {/* {heroMobileButtons && (
            <MobileButtonGroup>
              {heroMobileButtons.map((button) => (
                <MobileButton
                  key={button.title}
                  to={button.target.slug}
                  extension={button.linkExtension}
                >
                  {button.title}
                </MobileButton>
              ))}
            </MobileButtonGroup>
          )} */}
        </Copy>
      </MainWrapper>
      {heroVideoType === 'Small hero video' &&
        heroVideo &&
        heroVideoCopy &&
        heroVideoLink && (
          <Technology
            to={heroVideoLink.target.slug}
            extension={heroVideoLink.linkExtension}
          >
            <StyledSmallHeroVideo
              webm={heroVideo.videoWebm.file.url}
              mp4={heroVideo.videoMp4.file.url}
              poster={heroVideo.poster.fluid}
            />
            <TechnologyWrapper>
              <TechnologyCopy>{heroVideoCopy}</TechnologyCopy>
              <TechnologyButtonCopy>{heroVideoLink.title}</TechnologyButtonCopy>
            </TechnologyWrapper>
          </Technology>
        )}
    </StyledHero>
  )
}

const DesktopVideoContainer = styled.div`
  display: none;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: block;
  }
`
const MobileVideoContainer = styled.div`
  display: block;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: none;
  }
`

const StyledHero = styled.section`
  height: 100vw;
  position: relative;
  flex-direction: column;
  margin-top: ${(props) => props.theme.headerHeight.mobile};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 27vw;
    margin-top: ${(props) => props.theme.headerHeight.desktop};
  }
`

const MainWrapper = styled.div`
  position: relative;
  height: 100%;

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    overflow: hidden;
  }
`

const StyledFullHeroVideo = styled(Video)`
  position: absolute;
  z-index: -1;
  top: 0;
  bottom: 0;
  left: 0;

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    video {
      height: auto !important;
      width: 100% !important;
      position: absolute !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%);
    }
  }
`

const StyledImage = styled(GatsbyImage)`
  height: 100vw;
  width: 100%;
  object-fit: cover;
  display: flex;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: 100%;
    width: 100%;
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    height: auto;
  }
`

const StyledMobileImage = styled(GatsbyImage)`
  height: 100vw;
  width: 100%;
  object-fit: cover;
  display: flex;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: none;
  }
`

const Copy = styled.div`
  position: absolute;
  bottom: ${(props) => props.theme.space.marginMobile * 4}vw;
  text-align: ${(props) =>
    props.heroVideoType === 'Full-hero video' ? 'left' : 'center'};
  top: ${(props) => (props.heroVideoType === 'Full-hero video' ? '52%' : null)};
  left: ${(props) =>
    props.heroVideoType === 'Full-hero video' ? '45%' : null};
  transform: ${(props) =>
    props.heroVideoType === 'Full-hero video' ? 'translate(-50%, -50%)' : null};

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding-left: ${(props) => props.theme.space.marginDesktop * 12}vw;
    max-width: 49vw;
    text-align: left;
    height: 100%;
    bottom: 0;
    display: flex;
    align-items: start;
    flex-direction: column;
    justify-content: center;
    top: auto;
    left: auto;
    transform: none;
  }
`

const CopyHeader = styled.h1`
  font-size: 10vw;
  color: #${(props) => (props.color ? props.color : 'ffffff')};
  text-align: inherit;
  line-height: 12vw;
  width: 84vw;
  padding: 0
    ${(props) =>
      props.heroVideoType === 'Full-hero video'
        ? 0
        : props.theme.space.marginMobile * 2}vw
    ${(props) => props.theme.space.marginMobile * 2}vw;
  overflow: hidden;
  white-space: pre-line;
  text-shadow: 5px 1px 12px #0000007d;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    text-align: left;
    font-size: 5.9vw;
    line-height: 6vw;
    padding: 0 0 ${(props) => props.theme.space.marginDesktop * 4}vw;
  }

  @media screen and (-ms-high-contrast: active) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}),
    screen and (-ms-high-contrast: none) and (min-width: ${(props) =>
      props.theme.breakPoints.desktop}) {
    width: 100%;
  }
`
const SoundButton = styled.button`
  position: absolute;
  bottom: ${mobileVW(-110)};
  left: ${mobileVW(2)};
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    position: absolute;
    bottom: ${desktopVW(20)};
    left: ${desktopVW(20)};
  }
  svg {
    fill: white;
    width: ${desktopVW(100)};
    height: ${desktopVW(100)};
    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      fill: white;
      width: ${desktopVW(35)};
      height: ${desktopVW(35)};
    }
  }
`
const MobileButtonGroup = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
  text-align: center;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: none;
  }
`

const MobileButton = styled(SiteLink)`
  width: 25vw;
  background: white;
  color: #232323;
  font-size: 2.8vw;
  text-decoration: none;
  letter-spacing: 0.05em;
  margin: 1vw;
  padding: ${(props) => props.theme.space.marginMobile * 1.2}vw
    ${(props) => props.theme.space.marginMobile * 2.5}vw;
`

const CopyButton = styled(SiteLink)`
  text-decoration: none;
  background: white;
  color: #232323;
  font-size: 2.8vw;
  padding: ${(props) => props.theme.space.marginMobile * 1.2}vw
    ${(props) => props.theme.space.marginMobile * 2.5}vw;
  font-family: ${(props) => props.theme.fontFamily.medium};
  letter-spacing: 0.05em;
  &.hide-on-mobile {
    display: none;
  }
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    display: inline-block;
    font-size: 0.85vw;
    padding: ${(props) => props.theme.space.marginDesktop * 2}vw
      ${(props) => props.theme.space.marginDesktop * 4}vw;
    letter-spacing: 0.05em;
  }
`

const Technology = styled(SiteLink)`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  display: block;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    position: absolute;
    max-height: 15vw;
    max-width: 32vw;
    min-width: 32vw;
    bottom: ${(props) => props.theme.space.marginDesktop * 5}vw;
    right: 0;
  }
`

const TechnologyWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  color: white;
  text-align: left;
  padding: ${(props) => props.theme.space.marginMobile * 3.3}vw
    ${(props) => props.theme.space.marginMobile * 3.5}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    padding: ${(props) => props.theme.space.marginDesktop * 6}vw
      ${(props) => props.theme.space.marginDesktop * 10}vw
      ${(props) => props.theme.space.marginDesktop * 6}vw
      ${(props) => props.theme.space.marginDesktop * 6}vw;
    text-align: right;
    left: auto;
  }
`

const StyledSmallHeroVideo = styled(Video)`
  object-fit: cover;
  height: 100%;
  width: 100%;
`

const TechnologyCopy = styled.h2`
  font-size: ${(props) => props.theme.fontSize.m30};
  padding-bottom: ${(props) => props.theme.space.marginMobile}vw;
  line-height: 6vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d20};
    line-height: 1.4;
    text-align: left;
    padding-bottom: ${(props) => props.theme.space.marginDesktop * 2}vw;
  }
`

const TechnologyButtonCopy = styled.div`
  font-size: ${(props) => props.theme.fontSize.m23};
  padding-bottom: ${(props) => props.theme.space.marginMobile / 4}vw;
  border-bottom: 1px solid white;
  display: inline-block;
  width: fit-content;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: ${(props) => props.theme.fontSize.d14};
    padding-bottom: ${(props) => props.theme.space.marginDesktop}vw;
    padding-bottom: ${(props) => props.theme.space.marginDesktop / 4}vw;
  }
`

export default HeroBlock
